import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ApiError } from '@rydres/api-client';
import { BRAND, type Point } from '@rydres/shared';
import { AddressInput, Button, Field, Input, MapPreview, Notice, PlaceButtons, Spinner, TextArea, driveText, fmtPhone, formatUsd, toE164, useAddressTarget, useMyLocation, useRoute, type PickedAddress } from '@rydres/ui';
import { api, geoApi, STRIPE_PUBLISHABLE_KEY } from '../lib/api.js';
import type { BusyRange, PublicBookingView, PublicPage } from '../lib/types.js';
import { AppBadges, appLinksShown } from './AppBadges.js';
import { Backups } from './Backups.js';
import { usePassenger } from '../lib/passenger.js';
import { withinHours } from '@rydres/shared';

const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

/** Local wall-clock input → ISO with the browser's offset. Riders book in their own time zone. */
const toIso = (local: string) => (local ? new Date(local).toISOString() : '');
const minLocal = (hoursAhead: number) => {
  const d = new Date(Date.now() + hoursAhead * 3_600_000);
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function BookingPage() {
  const { handle = '' } = useParams();
  const [page, setPage] = useState<PublicPage | null | undefined>(undefined);
  useEffect(() => {
    api
      .call<PublicPage>('GET', `/public/pages/${encodeURIComponent(handle)}`, { auth: false })
      .then(setPage, () => setPage(null));
  }, [handle]);

  if (page === undefined) return <Center><Spinner /></Center>;
  if (page === null) return <Center><p className="text-muted">There is no booking page at this address.</p></Center>;
  return <Page page={page} />;
}

function Center({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center p-6">{children}</div>;
}

function Page({ page }: { page: PublicPage }) {
  const navigate = useNavigate();
  // ?service=<id> or custom: chosen on the passenger's Services page.
  const wanted = useSearchParams()[0].get('service');
  const [serviceId, setServiceId] = useState(wanted === 'custom' ? 'custom' : (page.services.find((s) => s.id === wanted)?.id ?? page.services[0]?.id ?? ''));
  const custom = serviceId === 'custom';
  const service = custom ? undefined : (page.services.find((s) => s.id === serviceId) ?? page.services[0]);
  const [description, setDescription] = useState('');
  const [busy, setBusyRanges] = useState<BusyRange[]>([]);
  useEffect(() => {
    const from = new Date().toISOString();
    const to = new Date(Date.now() + page.maxAdvanceDays * 86_400_000).toISOString();
    api
      .call<{ busy: BusyRange[] }>('GET', `/public/pages/${encodeURIComponent(page.handle)}/availability`, { auth: false, query: { from, to } })
      .then((r) => setBusyRanges(r.busy), () => undefined);
  }, [page.handle, page.maxAdvanceDays]);
  const canCard = page.acceptedModes.includes('card') && Boolean(stripePromise);
  const canDirect = page.acceptedModes.some((m) => m !== 'card');
  const [mode, setMode] = useState<'card' | 'direct'>(canCard ? 'card' : 'direct');
  const [form, setForm] = useState({
    startsAt: '',
    passengers: 1,
    pickupAddress: '',
    dropoffAddress: '',
    pickupPoint: null as Point | null,
    dropoffPoint: null as Point | null,
    riderName: '',
    riderPhone: '',
    riderEmail: '',
    notes: '',
  });
  const passenger = usePassenger();
  const [shareMine, setShareMine] = useState(true);
  useEffect(() => {
    const me = passenger.me;
    if (me) setForm((f) => ({ ...f, riderName: f.riderName || me.details.name || '', riderPhone: f.riderPhone || me.phone, riderEmail: f.riderEmail || me.details.email || '' }));
  }, [passenger.me]);
  const [error, setError] = useState<string | null>(null);
  const [sending, setBusy] = useState(false);
  const [paying, setPaying] = useState<PublicBookingView | null>(null);
  const slotWarning = (() => {
    if (!form.startsAt) return null;
    const start = new Date(form.startsAt);
    if (Number.isNaN(start.getTime())) return null;
    if (!withinHours(page.hours, start, page.timezone)) return 'The driver does not take pickups at that time.';
    const end = new Date(start.getTime() + ((service?.durationMin ?? 60) + 30) * 60_000);
    const hit = busy.find((b) => new Date(b.start) < end && new Date(b.end) > start);
    return hit ? 'The driver is already booked around that time. Try another time.' : null;
  })();
  const set = (k: keyof typeof form, v: string | number) => setForm((f) => ({ ...f, [k]: v }));
  const setPlace = (field: 'pickup' | 'dropoff', p: PickedAddress) =>
    setForm((f) => ({ ...f, [`${field}Address`]: p.address, [`${field}Point`]: p.point }));
  const address = useAddressTarget();
  const here = useMyLocation(geoApi);
  const route = useRoute(geoApi, form.pickupPoint, form.dropoffPoint);
  // A service's own addresses fill in when it is chosen, unless the rider already typed something
  // else; the place buttons then fill whichever address the service leaves open.
  const lastService = useRef<typeof service>(undefined);
  useEffect(() => {
    const prev = lastService.current;
    lastService.current = service;
    setForm((f) => {
      const pickup = !f.pickupAddress || f.pickupAddress === prev?.pickupAddress;
      const dropoff = !f.dropoffAddress || f.dropoffAddress === prev?.dropoffAddress;
      return {
        ...f,
        pickupAddress: pickup ? (service?.pickupAddress ?? '') : f.pickupAddress,
        pickupPoint: pickup ? (service?.pickupPoint ?? null) : f.pickupPoint,
        dropoffAddress: dropoff ? (service?.dropoffAddress ?? '') : f.dropoffAddress,
        dropoffPoint: dropoff ? (service?.dropoffPoint ?? null) : f.dropoffPoint,
      };
    });
    address.setTarget(service?.pickupAddress && !service.dropoffAddress ? 'dropoff' : 'pickup');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id]);
  const places = [...(passenger.me?.places ?? []), ...page.places].filter(
    (p, i, all) => all.findIndex((q) => q.label === p.label && q.address === p.address) === i,
  );
  const pick = (value: string, point: Point | null) => {
    setPlace(address.target, { address: value, point });
    if (address.target === 'pickup' && !form.dropoffAddress) address.setTarget('dropoff');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const phone = toE164(form.riderPhone);
    if (!phone) return setError('Enter a valid mobile number so the driver can reach you.');
    if (!form.startsAt) return setError('Pick a pickup date and time.');
    if (slotWarning) return setError(slotWarning);
    if (!custom && !service) return;
    setBusy(true);
    try {
      const common = {
        riderName: form.riderName,
        riderPhone: phone,
        riderEmail: form.riderEmail || null,
        passengers: Number(form.passengers),
        pickupAddress: form.pickupAddress,
        dropoffAddress: form.dropoffAddress,
        pickupPoint: form.pickupPoint,
        dropoffPoint: form.dropoffPoint,
        startsAt: toIso(form.startsAt),
        notes: form.notes || null,
      };
      const r = custom
        ? await api.call<PublicBookingView>('POST', `/public/pages/${encodeURIComponent(page.handle)}/quotes`, {
            auth: false,
            idempotent: true,
            body: { ...common, description: description.trim() || undefined },
          })
        : await api.call<PublicBookingView>('POST', `/public/pages/${encodeURIComponent(page.handle)}/bookings`, {
            auth: false,
            idempotent: true,
            body: { ...common, serviceId: service!.id, paymentMode: mode },
          });
      if (passenger.me && shareMine) await passenger.share(page.handle, true).catch(() => undefined);
      if (r.clientSecret) setPaying(r);
      else navigate(`/b/${r.booking.manageToken}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const vehicle = [page.vehicle.year, page.vehicle.color, page.vehicle.make, page.vehicle.model].filter(Boolean).join(' ');

  return (
    <div className="mx-auto max-w-xl px-4 pb-16 pt-6">
      <header className="flex items-start gap-4">
        {page.photoUrl ? (
          <img src={page.photoUrl} alt="" className="h-16 w-16 rounded-2xl object-cover" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand text-2xl font-black text-on-brand">
            {(page.displayName ?? '?').slice(0, 1)}
          </div>
        )}
        <div className="min-w-0">
          <h1 className="text-2xl font-black leading-tight">{page.displayName}</h1>
          <p className="text-sm text-ink2">{page.legalName}</p>
          <p className="text-xs text-muted">CPUC permit {page.tcpNumber}{vehicle ? ` · ${vehicle}` : ''}{page.vehicle.seats ? ` · up to ${page.vehicle.seats} passengers` : ''}</p>
        </div>
      </header>
      {page.bio && <p className="mt-4 text-sm text-ink2">{page.bio}</p>}
      {page.serviceArea && <p className="mt-1 text-xs text-muted">Serving {page.serviceArea}</p>}
      {page.vehiclePhotoUrl && <img src={page.vehiclePhotoUrl} alt={vehicle || 'The car'} className="mt-4 aspect-[16/9] w-full rounded-2xl object-cover" />}

      {!page.live && (
        <div className="mt-6">
          <Notice tone="warn">This page is not taking bookings right now. Call {fmtPhone(page.phone)} instead.</Notice>
          <Backups list={page.backups} driverName={page.displayName} />
        </div>
      )}

      {page.live && !paying && (
        <form onSubmit={submit} className="mt-6 space-y-5">
          <section>
            <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">Choose a service</h2>
            <div className="space-y-2">
              {page.services.map((s) => (
                <label
                  key={s.id}
                  className={`flex cursor-pointer items-start justify-between gap-3 rounded-2xl border p-4 ${serviceId === s.id ? 'border-accent bg-accent-soft/40 ring-1 ring-accent' : 'border-line bg-card'}`}
                >
                  <input type="radio" className="sr-only" name="service" checked={serviceId === s.id} onChange={() => setServiceId(s.id)} />
                  <div>
                    <div className="font-bold">{s.name}</div>
                    {s.description && <div className="text-sm text-ink2">{s.description}</div>}
                    <div className="mt-1 text-xs text-muted">Up to {s.maxPassengers} passengers · per vehicle</div>
                  </div>
                  <div className="text-lg font-black">{formatUsd(s.priceCents)}</div>
                </label>
              ))}
              <label className={`flex cursor-pointer items-start justify-between gap-3 rounded-2xl border p-4 ${custom ? 'border-accent bg-accent-soft/40 ring-1 ring-accent' : 'border-line bg-card'}`}>
                <input type="radio" className="sr-only" name="service" checked={custom} onChange={() => setServiceId('custom')} />
                <div>
                  <div className="font-bold">Something else</div>
                  <div className="text-sm text-ink2">Hourly, multiple stops, an event. Describe it and {page.displayName} will text you a price.</div>
                </div>
                <div className="text-sm font-bold text-muted">Quote</div>
              </label>
            </div>
          </section>
          {custom && (
            <Field label="Tell the driver about the trip (optional)" hint="Fill in the pickup, drop-off and time below; add anything that affects the price here.">
              <TextArea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Anything that affects the price: hours, stops, waiting, luggage, car seats." />
            </Field>
          )}

          <section className="grid gap-3 sm:grid-cols-2">
            <Field label="Pickup date and time" error={slotWarning} hint={page.minNoticeHours ? `At least ${page.minNoticeHours} hours ahead` : undefined}>
              <Input type="datetime-local" required min={minLocal(page.minNoticeHours)} value={form.startsAt} onChange={(e) => set('startsAt', e.target.value)} />
            </Field>
            {slotWarning && <Backups list={page.backups} driverName={page.displayName} title={`Or book a driver ${page.displayName ?? 'this driver'} trusts`} />}
            <Field label="Passengers">
              <Input type="number" min={1} max={service?.maxPassengers ?? page.vehicle.seats ?? 14} value={form.passengers} onChange={(e) => set('passengers', Number(e.target.value))} />
            </Field>
            <div className="space-y-2">
              <Field label="Pickup address">
                <AddressInput required geo={geoApi} near={form.dropoffPoint} placeholder="Terminal, hotel or street address" value={form.pickupAddress} onChange={(p) => setPlace('pickup', p)} onFocus={() => address.setTarget('pickup')} />
              </Field>
              {here.supported && (
                <button type="button" disabled={here.busy} onClick={() => void here.locate().then((p) => p && setPlace('pickup', p))} className="text-sm font-bold text-accent-text underline disabled:opacity-50">
                  {here.busy ? 'Finding you…' : 'Use my location'}
                </button>
              )}
              {here.error && <p className="text-xs text-danger">{here.error}</p>}
            </div>
            <Field label="Drop-off address">
              <AddressInput required geo={geoApi} near={form.pickupPoint} placeholder="Where to" value={form.dropoffAddress} onChange={(p) => setPlace('dropoff', p)} onFocus={() => address.setTarget('dropoff')} />
            </Field>
            {(form.pickupPoint || form.dropoffPoint) && (
              <div className="space-y-1 sm:col-span-2">
                <MapPreview from={form.pickupPoint} to={form.dropoffPoint} line={route?.polyline} />
                {route && <p className="text-xs text-muted">{driveText(route)}</p>}
              </div>
            )}
            {(places.length > 0 || (passenger.open && !passenger.me && !passenger.loading)) && (
              <div className="space-y-2 sm:col-span-2">
                <PlaceButtons places={places} target={address.target} onPick={pick} />
                {passenger.open && !passenger.me && !passenger.loading && (
                  <p className="text-xs text-muted">
                    <Link className="font-bold underline" to={`/me?back=/${encodeURIComponent(page.handle)}`}>Sign in</Link> to use your saved places.
                  </p>
                )}
              </div>
            )}
            <Field label="Your name">
              <Input required autoComplete="name" value={form.riderName} onChange={(e) => set('riderName', e.target.value)} />
            </Field>
            <Field label="Mobile number" note="For booking texts from the driver (confirmation, reminder, changes)">
              <Input required inputMode="tel" autoComplete="tel" value={form.riderPhone} onChange={(e) => set('riderPhone', e.target.value)} />
            </Field>
            <Field label="Email (optional)" hint="For your receipt">
              <Input type="email" autoComplete="email" value={form.riderEmail} onChange={(e) => set('riderEmail', e.target.value)} />
            </Field>
            {passenger.me && (
              <label className="flex items-start gap-2 text-sm text-ink2">
                <input type="checkbox" className="mt-1" checked={shareMine} onChange={(e) => setShareMine(e.target.checked)} />
                <span>Share my {BRAND.name} details with {page.displayName}, so they stay up to date for my next ride</span>
              </label>
            )}
            <Field label="Notes (optional)">
              <TextArea placeholder="Flight number, bags, child seat…" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </Field>
          </section>

          {!custom && canCard && canDirect && (
            <section>
              <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">How you'll pay</h2>
              <div className="flex gap-2 rounded-2xl bg-card2 p-1">
                {(['card', 'direct'] as const).map((m) => (
                  <button key={m} type="button" onClick={() => setMode(m)} className={`flex-1 rounded-xl px-3 py-2 text-sm font-bold ${mode === m ? 'bg-card shadow-card' : 'text-muted'}`}>
                    {m === 'card' ? 'Card now' : 'Pay the driver directly'}
                  </button>
                ))}
              </div>
              {mode === 'direct' && (
                <p className="mt-2 text-xs text-muted">
                  The driver confirms your request and you pay them {page.zelleHandle ? `by Zelle (${page.zelleHandle}), cash` : 'in cash'} or as agreed.
                </p>
              )}
            </section>
          )}

          <div className="rounded-2xl border border-line bg-card p-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-ink2">{custom ? 'Custom trip' : service?.name}</span>
              <span className="text-xl font-black">{custom ? 'Price by text' : service ? formatUsd(service.priceCents) : ''}</span>
            </div>
            <p className="mt-2 text-xs text-muted">{page.cancelPolicyText}</p>
            <p className="mt-1 text-xs text-muted">
              Your contract is with {page.legalName ?? page.displayName}, a licensed California charter-party carrier (TCP {page.tcpNumber}). {BRAND.name} provides the booking software only.
            </p>
            <p className="mt-2 text-xs text-muted">
              By booking you agree to receive text messages about this booking from your driver through {BRAND.name} (confirmation, reminder, changes). Message and data rates may apply. Message frequency varies by booking. Reply STOP to stop, HELP for help. See our <a className="underline" href="/privacy">privacy policy</a> and <a className="underline" href="/terms">terms</a>.
            </p>
          </div>

          {error && <Notice tone="danger">{error}</Notice>}
          <Button type="submit" disabled={sending || (!custom && !service) || Boolean(slotWarning)} className="w-full">
            {sending ? 'One moment…' : custom ? 'Ask for a price' : mode === 'card' ? `Continue to payment · ${service ? formatUsd(service.priceCents) : ''}` : 'Request this ride'}
          </Button>
        </form>
      )}

      {paying && stripePromise && (
        <div className="mt-6 space-y-4">
          <div className="rounded-2xl border border-line bg-card p-4 text-sm">
            <div className="font-bold">{service?.name}</div>
            <div className="text-ink2">{new Date(paying.booking.startsAt).toLocaleString()} · {paying.booking.pickupAddress} → {paying.booking.dropoffAddress}</div>
            <div className="mt-1 text-lg font-black">{formatUsd(paying.booking.priceCents ?? 0)}</div>
          </div>
          <Elements stripe={stripePromise} options={{ clientSecret: paying.clientSecret!, appearance: { theme: 'stripe', variables: { colorPrimary: '#2563eb', borderRadius: '12px' } } }}>
            <PayForm view={paying} />
          </Elements>
          <button type="button" className="w-full text-sm text-muted" onClick={() => setPaying(null)}>
            Back
          </button>
        </div>
      )}

      {appLinksShown() && (
        <div className="mt-10 flex flex-col items-center gap-2 text-center">
          <p className="text-sm text-muted">Book {page.displayName ?? 'this driver'} faster next time with the {BRAND.name} app</p>
          <AppBadges small />
        </div>
      )}
      <footer className="mt-12 text-center text-xs text-muted">
        Questions? Call {fmtPhone(page.phone)}. Powered by {BRAND.name}, operated by {BRAND.operator} · <a className="underline" href="/privacy">Privacy Policy</a> · <a className="underline" href="/terms">Terms of Use</a>
      </footer>
    </div>
  );
}

function PayForm({ view }: { view: PublicBookingView }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pay = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    const { error: err } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${location.origin}/b/${view.booking.manageToken}?paid=1` },
    });
    if (err) {
      setError(err.message ?? 'Payment did not go through.');
      setBusy(false);
    }
  };
  return (
    <form onSubmit={pay} className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && <Notice tone="danger">{error}</Notice>}
      <Button type="submit" disabled={busy || !stripe} className="w-full">
        {busy ? 'Processing…' : `Pay ${formatUsd(view.booking.priceCents ?? 0)}`}
      </Button>
      <p className="text-center text-xs text-muted">Charged by {view.driver.legalName ?? view.driver.displayName}. Apple Pay and Google Pay appear when your device supports them.</p>
    </form>
  );
}
